import React, { useState, useEffect } from 'react';
import { Row, Col, Toast, ToastContainer, Spinner } from 'react-bootstrap';
import { useAuth } from 'providers/AuthProvider';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { GET_SELLER_PROFILE } from 'api/graphql/queries/getSellerProfileQuery';
import SellerProfileCard from 'components/cards/SellerProfileCard';
import CompanyContactCard from 'components/cards/CompanyContactCard';
// Import our enhanced component with document upload tab
import SellerProfileEditorWithDocs from './SellerProfileEditorWithDocs';
import { useProfileUpdater } from 'hooks/useProfileUpdater';

const SellerProfileManager = () => {
  const { t } = useTranslation();
  const { seller, setSeller } = useAuth();
  
  // Query to get the latest profile data
  const { data, loading, error, refetch } = useQuery(GET_SELLER_PROFILE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  
  // Local state for profile info
  const [sellerInfo, setSellerInfo] = useState({
    shop_title: seller?.shop_title || "",
    company_description: seller?.company_description || "",
  });
  
  // Update local state when fresh data comes from the query
  useEffect(() => {
    if (data?.getSellerProfile?.data) {
      const freshData = data.getSellerProfile.data;
      
      // Update the authenticated seller object
      if (setSeller) {
        const updatedSeller = {
          ...JSON.parse(localStorage.getItem("seller") || "{}"),
          ...freshData,
        };
        localStorage.setItem("seller", JSON.stringify(updatedSeller));
        setSeller(updatedSeller);
      }
      
      // Update local state
      setSellerInfo({
        shop_title: freshData.shop_title || seller?.shop_title || "",
        company_description: freshData.company_description || seller?.company_description || "",
      });
    }
  }, [data, setSeller, seller]);
  
  // Use the ProfileUpdater hook for centralized update management
  const {
    updateField,
    trackChange,
    showToast,
    setShowToast,
    toastMessage,
    toastVariant,
    isSubmitting,
    refetchProfileData
  } = useProfileUpdater();
  
  // Function to trigger refresh of profile data - uses the centralized refetch
  const refreshProfileData = () => {
    refetchProfileData();
  };

  // Handler for profile text information updates (shop title, description)
  const handleUpdateSellerInfo = (
    updatedTitle: string,
    updatedDescription: string
  ) => {
    // Update local state for immediate UI feedback
    setSellerInfo({
      shop_title: updatedTitle,
      company_description: updatedDescription,
    });
  };

  // Handler for image uploads
  const handleUpdateProfile = async (input: any) => {
    if (Object.keys(input).length > 0) {
      // Track each field change
      Object.entries(input).forEach(([key, value]) => {
        trackChange(key, value);
      });
    }
  };

  // If query is loading initially, show loading state
  if (loading && !seller) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  // If there's an error and no seller data at all, show error message
  if (error && !seller) {
    return (
      <div className="alert alert-danger">
        Failed to load profile data. Please try again later.
      </div>
    );
  }

  return (
    <div className="mb-9">
      {/* Page Title */}
      <Row className="align-items-center g-3 mb-4">
        <Col>
          <h2 className="mb-0">{t("sellerProfileTitle")}</h2>
        </Col>
      </Row>

      {/* Profile Cards */}
      <Row className="g-3 mb-6">
        {/* <Col xs={12} lg={8}> */}
          {seller && (
            <SellerProfileCard
              seller={{
                name: seller.name || '',
                company_locality: seller.company_locality || '',
                shop_url: seller.shop_url || '',
                shop_title: sellerInfo.shop_title,
                logo_pic: seller.logo_pic,
                banner_pic: seller.banner_pic,
                company_description: sellerInfo.company_description
              }}
              onUpdateSellerInfo={handleUpdateSellerInfo}
              onUpdateProfile={handleUpdateProfile}
              onProfileUpdated={refreshProfileData}
              isLoading={isSubmitting}
            />
          )}
        {/* </Col> */}
        {/* <Col xs={12} lg={4}>
          {seller && (
            <CompanyContactCard 
              seller={{
                company_locality: seller.company_locality || '',
                email: seller.email || '',
                contact_number: seller.contact_number || ''
              }} 
            />
          )}
        </Col> */}
      </Row>

      {/* 
        Using the new SellerProfileEditor component exclusively 
        This bypasses any ESLint issues in the original SellerProfileForms
      */}
      <SellerProfileEditorWithDocs 
        onUpdateSellerInfo={handleUpdateSellerInfo}
        onProfileUpdated={refreshProfileData} 
      />

      {/* Global Toast Notifications */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          bg={toastVariant === "success" ? "success" : "danger"}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default SellerProfileManager;
